import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { MapPin, Briefcase, Clock, ArrowLeft, Send, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { vacaturesData } from '../data/mockData';

const vacatureFaqData = [
  {
    question: 'Hoe verloopt het sollicitatieproces?',
    answer: 'Na ontvangst van uw sollicitatie nemen wij binnen 2 werkdagen contact met u op. Het proces bestaat uit een kennismakingsgesprek, een inhoudelijk interview en eventueel een assessment. Wij begeleiden u gedurende het gehele traject.',
  },
  {
    question: 'Wat zijn de arbeidsvoorwaarden?',
    answer: 'Wij bieden een competitief salaris, reiskostenvergoeding, pensioenopbouw en toegang tot diverse opleidingsmogelijkheden. De exacte voorwaarden worden besproken tijdens het sollicitatieproces en zijn afhankelijk van de specifieke functie.',
  },
  {
    question: 'Kan ik als ZZP\'er aan de slag?',
    answer: 'Ja, wij werken ook met zelfstandige professionals. Onze modelovereenkomst is een door de Belastingdienst goedgekeurd document. Hierin staan afspraken die de werkrelatie tussen een zzp\'er en opdrachtgever verduidelijken.',
  },
  {
    question: 'Hoe lang duurt een gemiddelde opdracht?',
    answer: 'De duur van opdrachten varieert van enkele maanden tot meer dan een jaar, afhankelijk van het type functie en de behoefte van de opdrachtgever. Bij detavast constructies is er de mogelijkheid om na een periode in vaste dienst te treden.',
  },
  {
    question: 'Wat als de functie niet helemaal bij mij past?',
    answer: 'Geen zorgen! Neem contact met ons op en wij kijken samen naar welke mogelijkheden er zijn. Wij hebben vaak meerdere vacatures die mogelijk beter aansluiten bij uw profiel en wensen.',
  },
  {
    question: 'Hoe worden mijn uren geregistreerd?',
    answer: 'Er zijn twee manieren om de uren door te geven: via het formulier dat u van ons ontvangt, of door uw uren per mail naar de opdrachtgever te sturen met Staffing Force in de cc. Als uw opdrachtgever akkoord geeft, is het geregeld.',
  },
  {
    question: 'Krijg ik begeleiding tijdens mijn opdracht?',
    answer: 'Absoluut! Bij Staffing Force krijgt elke professional een vaste contactpersoon die u gedurende de gehele opdracht begeleidt. Wij plannen regelmatig evaluatiemomenten om te zorgen dat alles naar wens verloopt.',
  },
];

const VacatureDetailPage = () => {
  const { id } = useParams();
  const vacature = vacaturesData.find((v) => v.id === parseInt(id));

  if (!vacature) {
    return (
      <main className="pt-28 min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Vacature niet gevonden</h2>
          <Link to="/vacatures" className="text-[#00897B] font-medium hover:underline">
            Terug naar alle vacatures
          </Link>
        </div>
      </main>
    );
  }

  // Get related vacatures (same discipline, exclude current)
  const relatedVacatures = vacaturesData
    .filter((v) => v.discipline === vacature.discipline && v.id !== vacature.id)
    .slice(0, 3);

  // JobPosting schema voor Google for Jobs
  const validThrough = new Date(new Date(vacature.date).getTime() + 90 * 24 * 60 * 60 * 1000).toISOString();
  const jobSchema = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: vacature.title,
    description: vacature.description,
    datePosted: vacature.date,
    validThrough,
    employmentType: vacature.type === 'Vast' ? 'FULL_TIME' : 'CONTRACTOR',
    identifier: {
      '@type': 'PropertyValue',
      name: 'Staffing Force',
      value: vacature.reference || `SF-${vacature.id}`,
    },
    industry: vacature.discipline,
    occupationalCategory: vacature.discipline,
    hiringOrganization: {
      '@type': 'Organization',
      name: 'Staffing Force',
      sameAs: 'https://www.staffingforce.nl',
      logo: 'https://customer-assets.emergentagent.com/job_staffforce-new-site/artifacts/8pffcrsv_image.png',
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: vacature.location,
        addressCountry: 'NL',
      },
    },
    directApply: true,
  };

  const pageTitle = `${vacature.title} – ${vacature.location} | Vacature Staffing Force`;
  const pageDescription = `${vacature.title} in ${vacature.location} via Staffing Force. ${vacature.type} ${vacature.discipline} functie op ${vacature.level} niveau. ${vacature.description.slice(0, 80)}...`;

  return (
    <main className="pt-28 min-h-screen bg-gray-50">
      <SEO title={pageTitle} description={pageDescription.slice(0, 160)} />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(jobSchema)}
        </script>
      </Helmet>
      {/* Header */}
      <section className="bg-[#0d1b2a] py-16 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#00897B]/10 rounded-full blur-3xl" />
        <div className="max-w-5xl mx-auto px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/vacatures"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 transition-colors"
            >
              <ArrowLeft size={16} />
              Terug naar vacatures
            </Link>
            <div className="flex flex-wrap gap-3 mb-4">
              <span className="px-3 py-1 bg-[#00897B]/20 text-[#00bfa5] text-xs font-medium rounded-full">
                {vacature.discipline}
              </span>
              <span className="px-3 py-1 bg-white/10 text-white/80 text-xs font-medium rounded-full">
                {vacature.type}
              </span>
              <span className="px-3 py-1 bg-white/10 text-white/80 text-xs font-medium rounded-full">
                {vacature.level}
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
              {vacature.title}
            </h1>
            <div className="flex flex-wrap items-center gap-6 text-white/60 text-sm">
              <span className="flex items-center gap-2">
                <MapPin size={16} className="text-[#00bfa5]" />
                {vacature.location}
              </span>
              <span className="flex items-center gap-2">
                <Briefcase size={16} className="text-[#00bfa5]" />
                {vacature.type}
              </span>
              <span className="flex items-center gap-2">
                <Clock size={16} className="text-[#00bfa5]" />
                {new Date(vacature.date).toLocaleDateString('nl-NL', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-10">
          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2 space-y-10"
          >
            {/* Description */}
            <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Over de functie</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                {vacature.description} In deze rol bent u verantwoordelijk voor het aansturen van
                diverse processen en draagt u bij aan de verdere professionalisering van de organisatie.
                U werkt samen met een gedreven team en rapporteert direct aan het management.
              </p>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Uw verantwoordelijkheden</h3>
              <ul className="space-y-2 text-gray-600 mb-6">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00897B] mt-2 flex-shrink-0" />
                  Het bewaken en optimaliseren van de dagelijkse operationele processen
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00897B] mt-2 flex-shrink-0" />
                  Het opstellen van rapportages en analyses voor het management
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00897B] mt-2 flex-shrink-0" />
                  Samenwerken met interne en externe stakeholders
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00897B] mt-2 flex-shrink-0" />
                  Bijdragen aan verbeterprojecten en procesoptimalisaties
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00897B] mt-2 flex-shrink-0" />
                  Het adviseren van het management op uw vakgebied
                </li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">Wat wij vragen</h3>
              <ul className="space-y-2 text-gray-600 mb-6">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00897B] mt-2 flex-shrink-0" />
                  HBO/WO opleiding in een relevante richting
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00897B] mt-2 flex-shrink-0" />
                  Minimaal 3-5 jaar ervaring in een vergelijkbare functie
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00897B] mt-2 flex-shrink-0" />
                  Uitstekende communicatieve vaardigheden in Nederlands en Engels
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00897B] mt-2 flex-shrink-0" />
                  Analytisch sterk en resultaatgericht
                </li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">Wat wij bieden</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00897B] mt-2 flex-shrink-0" />
                  Competitief salaris passend bij uw ervaring
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00897B] mt-2 flex-shrink-0" />
                  Professionele begeleiding gedurende de gehele opdracht
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00897B] mt-2 flex-shrink-0" />
                  Opleidingsmogelijkheden en persoonlijke ontwikkeling
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00897B] mt-2 flex-shrink-0" />
                  Reiskostenvergoeding en pensioenregeling
                </li>
              </ul>
            </div>

            {/* FAQ Section */}
            <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Veelgestelde vragen</h2>
              <p className="text-gray-500 text-sm mb-6">Alles wat u wilt weten over deze vacature en het sollicitatieproces.</p>
              <Accordion type="single" collapsible className="space-y-2">
                {vacatureFaqData.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`item-${index}`}
                    className="bg-gray-50 border border-gray-100 rounded-xl px-5 overflow-hidden data-[state=open]:bg-[#00897B]/5 data-[state=open]:border-[#00897B]/20 transition-colors"
                  >
                    <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#00897B] transition-colors py-4 text-sm hover:no-underline">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 leading-relaxed pb-4 text-sm">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </motion.div>

          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="lg:sticky lg:top-28 lg:self-start space-y-6"
          >
            {/* Apply Card */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h3 className="font-bold text-lg text-gray-900 mb-2">Interesse in deze functie?</h3>
              <p className="text-gray-500 text-sm mb-6">
                Solliciteer direct of neem contact op voor meer informatie.
              </p>
              <a
                href={`mailto:info@staffingforce.nl?subject=${encodeURIComponent(
                  `Sollicitatie: ${vacature.title}`,
                )}&body=${encodeURIComponent(
                  `Beste Staffing Force,\n\nIk heb interesse in de vacature "${vacature.title}" (${vacature.location}).\n\nGraag verneem ik meer over deze functie en het sollicitatieproces. Mijn CV stuur ik in een vervolgmail.\n\nMet vriendelijke groet,\n`,
                )}`}
                className="w-full bg-[#00897B] hover:bg-[#00796B] text-white py-3 rounded-lg font-semibold text-sm transition-all duration-300 hover:shadow-lg hover:shadow-[#00897B]/25 hover:-translate-y-0.5 flex items-center justify-center gap-2 mb-3"
              >
                <Send size={16} />
                Direct solliciteren
              </a>
              <a
                href={`https://wa.me/31205811000?text=Hallo, ik heb interesse in de vacature: ${vacature.title}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white py-3 rounded-lg font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Via WhatsApp
              </a>

              <div className="mt-6 pt-6 border-t border-gray-100">
                <h4 className="font-semibold text-gray-900 text-sm mb-3">Overzicht</h4>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Locatie</span>
                    <span className="font-medium text-gray-900">{vacature.location}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Type</span>
                    <span className="font-medium text-gray-900">{vacature.type}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Discipline</span>
                    <span className="font-medium text-gray-900">{vacature.discipline}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Niveau</span>
                    <span className="font-medium text-gray-900">{vacature.level}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Geplaatst</span>
                    <span className="font-medium text-gray-900">
                      {new Date(vacature.date).toLocaleDateString('nl-NL', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Related Vacatures */}
            {relatedVacatures.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <h3 className="font-bold text-lg text-gray-900 mb-4">Vergelijkbare vacatures</h3>
                <div className="space-y-3">
                  {relatedVacatures.map((rv) => (
                    <Link
                      key={rv.id}
                      to={`/vacatures/${rv.id}`}
                      className="block p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                    >
                      <h4 className="font-medium text-gray-900 text-sm group-hover:text-[#00897B] transition-colors flex items-center justify-between">
                        {rv.title}
                        <ChevronRight size={14} className="text-gray-400" />
                      </h4>
                      <p className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                        <MapPin size={12} />
                        {rv.location} • {rv.type}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </main>
  );
};

export default VacatureDetailPage;
